import { useQuery } from "@rocicorp/zero/react"
import { useNavigate } from "@tanstack/react-router"

import type { Membership } from "@productlane/zero"
import { isOutOfOffice } from "@productlane/lib/src/out-of-office"
import { Button, Switch } from "@productlane/ui"
import { queries } from "@productlane/zero/queries"

import {
	BackOnPicker,
	CoverPicker,
	formatBackOn,
	memberName,
	useCovers,
} from "@/components/out-of-office/out-of-office-pickers"
import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useOutOfOfficeDialog } from "@/stores/out-of-office-store"
import { SettingItem } from "../settings-switches/setting-item"
import { SettingsContainer } from "../settings-switches/settings-container"

const threadCount = (n: number) => `${n} ${n === 1 ? "thread" : "threads"}`

export function OutOfOfficeSettings({
	userId,
	membership,
}: {
	userId: string
	membership: Membership | null
}) {
	const navigate = useNavigate()
	const setDialogOpen = useOutOfOfficeDialog((state) => state.setOpen)
	const covers = useCovers(userId)
	const [teamMembers] = useQuery(queries.teamMembers())

	const until = membership?.outOfOfficeUntil ?? null
	const coverId = membership?.outOfOfficeCoverId ?? null
	const isAway = isOutOfOffice(until)
	const cover = teamMembers.find((member) => member.userId === coverId) ?? null
	const coverFirstName = memberName(cover).split(" ")[0]

	const utils = api.useUtils()
	const status = api.outOfOffice.status.useQuery(undefined, {
		enabled: isAway,
	})
	const update = api.outOfOffice.update.useMutation()
	const stop = api.outOfOffice.stop.useMutation()

	const save = async (input: { until?: Date; coverId?: string }) => {
		try {
			await update.mutateAsync(input)
			await utils.outOfOffice.status.invalidate()
			analytics.track("update-out-of-office")
		} catch {
			toast.error({ title: "Failed to save out of office" })
		}
	}

	const turnOff = async () => {
		try {
			await stop.mutateAsync()
			await utils.outOfOffice.return.invalidate()
			analytics.track("turn-off-out-of-office")
		} catch {
			toast.error({ title: "Failed to turn off out of office" })
		}
	}

	return (
		<>
			<SettingsContainer className="mt-8">
				<SettingItem
					title="Out of office"
					description={
						isAway && until && cover
							? `Away until ${formatBackOn(until)}. ${coverFirstName} covers your threads.`
							: undefined
					}
					action={
						<Switch
							name="outOfOffice"
							checked={isAway && !stop.isLoading}
							disabled={stop.isLoading}
							onCheckedChange={(on) => {
								if (on) setDialogOpen(true)
								else void turnOff()
							}}
						/>
					}
				/>

				{isAway && until && (
					<>
						<SettingItem
							title="Back on"
							description="Turns off on its own that morning, in your timezone."
							className="pt-4"
							action={
								<BackOnPicker
									value={new Date(until)}
									onChange={(value) => void save({ until: value })}
								/>
							}
						/>

						<SettingItem
							title="Covered by"
							description="Gets your new threads and customer replies while you're away."
							className="pt-4"
							action={
								<CoverPicker
									covers={covers}
									cover={cover}
									onChange={(userId) => void save({ coverId: userId })}
								/>
							}
						/>

						{cover && (
							<SettingItem
								title={`Moved to ${coverFirstName}`}
								description={`${threadCount(status.data?.heldByCover ?? 0)} waiting on you. ${status.data?.waitingOnCustomer ?? 0} waiting on the customer stay yours until they reply.`}
								className="pt-4"
								action={
									<Button
										type="button"
										variant="outline"
										onClick={() =>
											void navigate({
												to: "/members/$id",
												params: { id: cover.userId },
											})
										}
									>
										View threads
									</Button>
								}
							/>
						)}
					</>
				)}
			</SettingsContainer>
		</>
	)
}
